import type { Translation } from '../types';

/**
 * Russian has four plural categories (one / few / many / other), which is why
 * counted phrases are `PluralForms` rather than a single string: `1 карта`,
 * `2 карты`, `5 карт`.
 */
export const ru: Translation = {
  meta: { name: 'Русский', english: 'Russian', tag: 'ru', dir: 'ltr' },

  common: {
    menu: "Меню",
    cancel: 'Отмена',
    done: 'Готово',
    join: 'Войти',
    leave: 'Выйти',
    refresh: 'Обновить',
    save: 'Сохранить',
    you: 'вы',
    host: 'хост',
    ai: 'ИИ',
    ready: 'Готов',
    notReady: 'Не готов',
  },

  conn: {
    live: 'На связи',
    connecting: 'Подключение',
    offline: 'Нет связи',
    connectingFull: 'Подключение…',
    reconnectBanner: 'Соединение потеряно — переподключаемся. Место сохраняется 3 минуты.',
  },

  nickname: {
    pickTitle: 'Выберите имя',
    changeTitle: 'Изменить имя',
    subtitle: 'Так вас увидят другие игроки.',
    placeholder: 'например, Дикая четвёрка',
    label: 'Никнейм',
    letsPlay: 'Играть',
  },

  lobby: {
    heroTitle: 'Играйте в UNO с друзьями',
    heroSubtitle: 'Без регистрации. Поделитесь ссылкой — и раздавайте.',
    createRoom: 'Создать комнату',
    roomCode: 'Код комнаты',
    publicRooms: 'Открытые комнаты',
    empty: 'Открытых комнат пока нет — создайте первую.',
    cardMeta: 'хост {host} · ход {seconds} с',
    stacking: 'накопление',
    badgeOpen: 'Открыта',
    badgeFull: 'Заполнена',
    badgeLive: 'Идёт игра',
    lockedTitle: 'Комната {id} закрыта паролем',
    password: 'Пароль',
    passwordLabel: 'Пароль комнаты',
  },

  create: {
    title: 'Новая комната',
    roomName: 'Название комнаты',
    roomNamePlaceholder: 'UNO в пятницу вечером',
    maxPlayers: 'Максимум игроков',
    playersOption: {
      one: '{count} игрок',
      few: '{count} игрока',
      many: '{count} игроков',
      other: '{count} игроков',
    },
    passwordOptional: 'Пароль (необязательно)',
    passwordPlaceholder: 'Пусто — комната открыта для всех',
    listPublic: 'Показывать в списке открытых комнат',
    houseRules: 'Домашние правила',
    submit: 'Создать',
  },

  rules: {
    turnTimer: 'Время на ход',
    turnTimerHint: 'Когда оно закончится, бот сыграет за вас случайную допустимую карту.',
    startingHand: 'Карт на старте',
    unoPenalty: 'Штраф за UNO',
    stackDrawTwo: 'Накопление +2',
    stackDrawTwoHint: 'Ответить на +2 своей +2 вместо того, чтобы брать карты',
    stackDrawFour: 'Накопление +4',
    stackDrawFourHint: 'Ответить на +4 своей +4',
    challengeWildFour: 'Оспаривание +4',
    challengeWildFourHint: 'Следующий игрок может оспорить незаконную +4',
    strictWildFour: 'Строгая +4',
    strictWildFourHint: 'Играется только если нет карт активного цвета',
    playDrawn: 'Играть взятую карту',
    playDrawnHint: 'Взятую карту можно сыграть сразу',
    drawUntilPlayable: 'Брать до подходящей',
    drawUntilPlayableHint: 'Брать карты, пока не попадётся подходящая',
    hostOnly: 'менять может только хост',
  },

  room: {
    copyLink: 'Скопировать ссылку',
    copied: '✓ Скопировано',
    roomLabel: 'Комната',
    locked: 'с паролем',
    public: 'открытая',
    unlisted: 'скрытая',
    readyStatus: 'готовы {ready} из {total}',
    botsJoining: {
      one: 'присоединится {count} бот',
      few: 'присоединятся {count} бота',
      many: 'присоединятся {count} ботов',
      other: 'присоединятся {count} ботов',
    },
    startsWhenReady: 'игра начнётся, как только будут готовы все.',
    needMorePlayers: 'Для старта нужно {min} игрока — позовите кого-нибудь или добавьте бота.',
    playersTitle: 'Игроки',
    botsTitle: 'ИИ-боты',
    botsHostOnly: 'менять может только хост',
    noBots: 'Без ботов',
    botsWillJoin: {
      one: 'Будет играть {count} бот',
      few: 'Будут играть {count} бота',
      many: 'Будут играть {count} ботов',
      other: 'Будут играть {count} ботов',
    },
    botsHint: 'Они сядут за стол при старте игры. Сейчас поместится до {max}.',
    botsRoomFull: 'Комната заполнена — места для ботов нет.',
    soloCta: 'Играете один? Добавьте бота и начинайте',
    addBot: 'Добавить бота',
    removeBot: 'Убрать бота',
    removePlayer: 'Удалить {name}',
    botPlaceholder: 'Бот {n}',
    defaultRoomName: 'Комната {name}',
    imReady: 'Я готов',
    cancelReady: 'Отменить готовность',
    offlineTag: 'не в сети',
  },

  game: {
    yourTurn: 'Ваш ход',
    yourTurnStack: 'Ваш ход — накопите или возьмите {count}',
    othersTurn: 'Ходит {name}',
    waiting: 'Ожидание…',
    gameOver: 'Игра окончена',
    challengeWindow: 'Дикая +4 — можно оспорить',
    drawPileLeft: 'осталось {count}',
    discard: 'сброс',
    draw: 'Взять',
    takeCards: 'Взять {count}',
    pass: 'Пас',
    uno: 'UNO!',
    judge: 'УЛИЧИТЬ',
    cardCount: {
      one: '{count} карта',
      few: '{count} карты',
      many: '{count} карт',
      other: '{count} карт',
    },
    clockwise: 'По часовой стрелке',
    counterClockwise: 'Против часовой стрелки',
    activeColour: 'Активный цвет',
    noCards: 'Карт не осталось — отлично.',
    oneCard: 'одна карта!',
    unoTag: 'UNO',
    forfeitedTag: 'вышел',
    offlineFor: 'не в сети {seconds} с',
    placeTag: '{place}-е место',
    pickColour: 'Выберите цвет',
    challengeTitle: '{name} сыграл(а) Дикую +4. Оспорить?',
    challengeExplain: 'Если это был блеф, он(а) возьмёт {bluff}. Если нет — вы возьмёте {fail}.',
    challengeBtn: 'Оспорить',
    acceptBtn: 'Взять {count}',
    youWin: '🎉 Вы победили!',
    standingsTitle: 'Игра окончена',
    points: '{points} очк.',
    forfeited: 'вышел',
    backToLobby: 'В лобби',
    playAgain: 'Ещё партию',
    waitingOthers: 'Ждём остальных…',
    faceDownCard: 'Карта рубашкой вверх',
    sortByColour: "Сортировать по цвету",
    sortAsDealt: "Оставить как раздали",
    drawACard: 'Взять карту',
    drawNCards: 'Взять карт: {count}',
  },

  events: {
    someone: 'Кто-то',
    unoCalledYou: 'Вы объявили UNO!',
    unoCalledOther: '{name} объявил(а) UNO!',
    judged: '{accuser} уличил(а) {target} — +{count} карт',
    judgedYou: '{accuser} уличил(а) вас — +{count} карт',
    challengeBluff: 'Блеф раскрыт — {name} берёт {count}',
    challengeFailed: 'Оспаривание не удалось — {name} берёт {count}',
    forfeited: '{name} вышел(ла) из игры.',
    afkYou: 'Время вышло — ход сделан автоматически.',
    afkOther: 'У {name} вышло время — ход сделан автоматически.',
    hostChanged: 'Теперь хост — {name}.',
    reshuffled: 'Колода перетасована.',
    youWin: 'Вы победили!',
    winner: 'Побеждает {name}!',
    gameOver: 'Игра окончена.',
  },

  errors: {
    bad_request: 'Что-то пошло не так.',
    no_session: 'Сессия истекла — переподключаемся.',
    room_not_found: 'Такой комнаты нет.',
    room_full: 'Комната заполнена.',
    wrong_password: 'Неверный пароль.',
    already_in_room: 'Сначала выйдите из текущей комнаты.',
    not_in_room: 'Вы не в комнате.',
    not_host: 'Это может сделать только хост.',
    game_in_progress: 'Игра уже началась.',
    not_enough_players: 'Недостаточно игроков.',
    stale_action: 'Кто-то успел раньше — ваш ход отменён.',
    illegal_action: 'Такой ход недопустим.',
    rate_limited: 'Слишком часто.',
    nickname_invalid: 'Такой никнейм не подойдёт.',
  },

  theme: {
    title: 'Оформление карт',
    description: 'Недостающие изображения заменяются встроенной CSS-колодой.',
    builtIn: 'Встроено — файлы не нужны',
    pngHint: 'Положите PNG 300×450 в {path} и добавьте их в список тем.',
  },

  language: {
    title: 'Язык',
    description: 'Применяется сразу и запоминается на этом устройстве.',
  },

  cards: {
    red: "красная",
    yellow: "жёлтая",
    green: "зелёная",
    blue: "синяя",
    wild: "дикая",
    skip: "Пропуск",
    reverse: "Разворот",
    draw2: "+2",
    wildCard: "Дикая",
    wild4: "+4",
  },

  log: {
    title: "Ход игры",
    empty: "Пока пусто.",
    close: "Скрыть журнал",
    timestamps: "Время",
    chatPlaceholder: "Скажите что-нибудь…",
    send: "Отправить",
    played: "{name} сыграл(а) {card}",
    drew: "{name} взял(а) карт: {count}",
    skipped: "{name} пропускает ход",
    reversed: "Направление изменилось",
    joined: "{name} присоединился(ась)",
    left: "{name} вышел(ла)",
    started: "Игра началась",
  },

  profile: {
    avatar: "Аватар",
    avatarHint: "Выберите аватар или оставьте первую букву имени.",
  },

  leave: {
    title: "Выйти из игры?",
    body: "Партия ещё идёт. Если выйти, это засчитается как поражение, а карты вернутся в колоду.",
    stay: "Продолжить",
    confirm: "Всё равно выйти",
    unload: "Вы в разгаре партии.",
  },
};
